import React, { useState, useEffect, SyntheticEvent } from 'react';
import {
  MuiBox,
  MuiCheckbox,
  MuiFormControlLabel,
  MuiRadioGroup,
  MuiRadio
} from 'theme/material-ui';
import { FontSize, FontWeight, IconSize, Spacing } from 'modules/styles/variables';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import Typography from 'components/UI/Typography';
import { Icon } from 'components/Icon';
import Divider from 'components/UI/Divider';
import { useHistory } from 'react-router-dom';
import DefaultText from 'components/UI/Typography/DefaultText';
import FlexBox, { FlexBoxColumn, FlexBoxRow, FlexBoxDynamic } from 'components/UI/Layout/FlexBox';
import { Color } from 'modules/styles/colors';
import StyledButton from 'components/UI/Button/StyledButton';
import CameraButton from 'components/UI/Button/CameraButton';
import { StyledAsterisk } from 'components/RequiredFieldsLegend';
import { Insurance, CheckListItem, VisitDetail, PatientInfo } from 'store/preRegistration/types';
import {
  UPDATE_PRE_REGISTRATION_CHECKLIST,
  updateSkipPaymentFlag
} from 'store/preRegistration/actions';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'store/types';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import {
  PreRegistrationFetchingSelector,
  PreRegistrationCheckListStepByIdSelector,
  PreRegistrationPatientInfoSelector,
  PreRegistrationVisitDetailSelector,
  PreRegistrationSecondaryInsuranceSelector
} from 'store/preRegistration/selectors';
import {
  CardInfo,
  CardUploadPayload,
  InsuranceCardProps,
  UploadInsuranceProps,
  YesNo,
  CheckListItemPageProps
} from './types';
import { FileNameWithImage } from '../types';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { CloseBox, ImageBox, Img, RetakeText, TopLeft, Spin } from '../styled';
import { useMutation } from '@apollo/react-hooks';
import { UPDATE_CHECKLIST_INSURANCE_ID } from 'services/orchestration/mutations';
import Spacer from 'components/UI/Layout/Spacer';
import { SkipPaymentFlags } from 'lib/constants/preRegister';
import { headerWithJWT, handleError, handleVerifyUpload } from '../utils';
import { errors, selfPayInsuranceCodes, MAX_DIMENSIONS, MAX_FILE_SIZE } from '../constant';
import { PreRegConfirmModal, PreRegHeader } from '../common';
import { Warning } from 'components/Error/common';
import Config from 'react-native-config';

const ALLOW_UPLOAD = Config.PRE_REG_ALLOW_UPLOAD_INSURANCE === 'enabled';

const INFORMATION_IS_CORRECT_OPTIONS = [
  {
    label: 'Yes',
    value: 'Yes'
  },
  {
    label: 'No',
    value: 'No'
  }
];

export function getSubmitStatus(
  infoCorrect: YesNo,
  selfPay: boolean,
  hasSecondaryImages: boolean,
  payload?: CardUploadPayload
): boolean {
  let rtn = false;

  const hasPrimaryCardFront = !!payload?.primary?.cardFront?.azureFileName;
  const hasPrimaryCardBack = !!payload?.primary?.cardBack?.azureFileName;
  const hasSecondaryCardFront = !!payload?.secondary?.cardFront?.azureFileName;
  const hasSecondaryCardBack = !!payload?.secondary?.cardBack?.azureFileName;

  const hasPrimary = hasPrimaryCardFront && hasPrimaryCardBack;
  const hasSecondary = hasSecondaryCardFront && hasSecondaryCardBack;

  const primaryLoading =
    (!hasPrimaryCardFront && payload?.primary?.cardFront?.base64) ||
    (!hasPrimaryCardBack && payload?.primary?.cardBack?.base64);

  if (infoCorrect === 'yes') {
    rtn = true;
  } else if (selfPay) {
    rtn = true;
  } else if (!hasSecondaryImages) {
    rtn = hasPrimary;
  } else if (hasSecondaryImages) {
    const hasBoth = hasPrimary && hasSecondary;
    const hasOnlySecondary =
      !primaryLoading && hasSecondary && !hasPrimaryCardFront && !hasPrimaryCardBack;
    // It should be falsy if we have both secondary images and any partial primary
    rtn = hasBoth || hasOnlySecondary;
  }
  return rtn;
}

export function InsuranceCard(props: InsuranceCardProps) {
  const { insurance } = props;
  const isSecondary = insurance.insuranceOrder !== 1;
  const { groupID, memberID, payerName } = insurance;
  return (
    <FlexBoxColumn data-testid="insurance-card" spacing={Spacing.large}>
      {isSecondary ? (
        <>
          <Divider color={Color.grayHue2} />
          <Spacer size="medium" />
        </>
      ) : null}
      <DefaultText fontWeight={FontWeight.bold}>
        {isSecondary ? 'Secondary Insurance' : 'Primary Insurance'}
      </DefaultText>
      <MuiBox margin={`${Spacing.medium}px 0px`}>
        <div>
          <DefaultText fontSize={FontSize.small}>Insurance Provider</DefaultText>
        </div>
        <div>
          <DefaultText fontWeight="bold">{payerName}</DefaultText>
        </div>
      </MuiBox>
      <MuiBox margin={`${Spacing.medium}px 0px`}>
        <div>
          <DefaultText fontSize={FontSize.small}>Group ID</DefaultText>
        </div>
        <div>
          <DefaultText fontWeight="bold">{groupID || 'NA'}</DefaultText>
        </div>
      </MuiBox>
      <MuiBox margin={`${Spacing.medium}px 0px`}>
        <div>
          <DefaultText fontSize={FontSize.small}>Member ID</DefaultText>
        </div>
        <div>
          <DefaultText fontWeight="bold">{memberID}</DefaultText>
        </div>
      </MuiBox>
    </FlexBoxColumn>
  );
}

export function UploadInsurance(props: UploadInsuranceProps) {
  const dispatch = useDispatch();
  const { isPrimary, onChange } = props;
  const [frontFile, setFrontFile] = useState<FileNameWithImage | undefined>();
  const [backFile, setBackFile] = useState<FileNameWithImage | undefined>();
  const [verifiedFront, setVerifiedFront] = useState(false);
  const [verifiedBack, setVerifiedBack] = useState(false);

  useEffect(() => {
    onChange({
      cardFront: frontFile,
      cardBack: backFile
    });
  }, [frontFile, backFile]);

  const handleSetBackFile = (payload: FileNameWithImage) => {
    setBackFile(payload);
    setVerifiedBack(!!payload.azureFileName);
  };

  const handleSetFrontFile = (payload?: FileNameWithImage) => {
    setFrontFile(payload);
    setVerifiedFront(!!payload?.azureFileName);
  };

  const backFileUploaded = (newBackFile: string, filename?: string) =>
    handleVerifyUpload(handleSetBackFile, dispatch, newBackFile, filename);

  const frontFileUploaded = async (newFrontFile?: string, filename?: string) =>
    handleVerifyUpload(handleSetFrontFile, dispatch, newFrontFile, filename);

  return (
    <FlexBoxColumn spacing={Spacing.large} data-testid="upload-insurance">
      <MuiBox>
        <DefaultText fontWeight="bold" fontSize={FontSize.medium}>
          {isPrimary ? 'Upload Primary Insurance' : 'Upload Secondary Insurance'}
        </DefaultText>
      </MuiBox>
      <FlexBoxRow>
        <FlexBoxDynamic width="50%" justifyContent="center">
          {frontFile?.base64 ? (
            <ImageBox>
              <TopLeft verified={verifiedFront}>
                {verifiedFront ? (
                  <Icon name="CheckCircle'" color={Color.green} size={IconSize.small} />
                ) : (
                  <Spin>
                    <Icon name="cached" color={Color.purple} size={IconSize.small} />
                  </Spin>
                )}
              </TopLeft>

              <CloseBox>
                <Icon
                  name="close"
                  onClick={() => frontFileUploaded()}
                  size={IconSize.base}
                  color={Color.black}
                />
              </CloseBox>
              <Img src={frontFile.base64} backgroundColor={Color.grayLight} />
              <RetakeText textAlign="right" onClick={() => frontFileUploaded()}>
                Change
              </RetakeText>
            </ImageBox>
          ) : (
            <CameraButton
              data-testid="camera-button-front"
              id={`UploadInsurance-front-${Date.now()}`}
              text="Front of Insurance Card"
              onSuccess={frontFileUploaded}
              maxFileSize={MAX_FILE_SIZE}
              maxDimensions={MAX_DIMENSIONS}
            />
          )}
        </FlexBoxDynamic>
        <FlexBoxDynamic width="50%" justifyContent="center">
          {backFile?.base64 ? (
            <ImageBox>
              <TopLeft verified={verifiedBack}>
                {verifiedBack ? (
                  <Icon name="CheckCircle'" color={Color.green} size={IconSize.small} />
                ) : (
                  <Spin>
                    <Icon name="cached" color={Color.purple} size={IconSize.small} />
                  </Spin>
                )}
              </TopLeft>
              <CloseBox>
                <Icon
                  name="close"
                  onClick={() => backFileUploaded('')}
                  size={IconSize.base}
                  color={Color.black}
                />
              </CloseBox>
              <Img src={backFile.base64} backgroundColor={Color.grayLight} />
              <RetakeText textAlign="right" onClick={() => backFileUploaded('')}>
                Change
              </RetakeText>
            </ImageBox>
          ) : (
            <CameraButton
              data-testid="camera-button-back"
              id={`UploadInsurance-back-${Date.now()}`}
              text="Back of Insurance Card"
              onSuccess={backFileUploaded}
              maxFileSize={MAX_FILE_SIZE}
              maxDimensions={MAX_DIMENSIONS}
            />
          )}
        </FlexBoxDynamic>
      </FlexBoxRow>
    </FlexBoxColumn>
  );
}

export function composePayload(
  step: CheckListItem,
  visitDetail: VisitDetail,
  patientInfo: PatientInfo,
  isCorrect: string,
  payload?: CardUploadPayload,
  electSelfPay?: boolean
) {
  const images = [];
  const { primary, secondary } = payload || {};
  if (primary?.cardFront?.azureFileName) {
    const { cardFront, cardBack } = primary;
    images.push({
      name: 'Primary Insurance ID',
      description: 'Card front',
      azureFileName: cardFront.azureFileName
    });
    images.push({
      name: 'Primary Insurance ID',
      description: 'Card back',
      azureFileName: cardBack?.azureFileName
    });
  }
  if (secondary?.cardFront?.azureFileName) {
    const { cardFront, cardBack } = secondary;
    images.push({
      name: 'Secondary Insurance ID',
      description: 'Card front',
      azureFileName: cardFront.azureFileName
    });
    images.push({
      name: 'Secondary Insurance ID',
      description: 'Card back',
      azureFileName: cardBack?.azureFileName
    });
  }
  const fin = visitDetail.finNumber;
  const mrn = patientInfo?.id;
  const aliasId = step.docInfo.docId;

  let status = 'COMPLETE';
  if (electSelfPay) {
    status = 'SELF_PAY_ELECT';
  } else if (isCorrect === 'No') {
    status = 'SKIP';
  } else if (images.length) {
    status = 'CHANGE';
  }

  return {
    arType: visitDetail.arType,
    visitType: visitDetail.type,
    checkListStepId: step.id,
    facilityCode: visitDetail.facilityCode,
    fin,
    mrn,
    stepData: {
      electSelfPay: !!electSelfPay,
      mrn,
      fin,
      aliasId,
      isInsuranceCard: true,
      status,
      images
    }
  };
}

interface Props extends CheckListItemPageProps {
  secondaryInsuranceStep?: CheckListItem;
}

// TODO How are we handling out of network
export function PreRegisterationInsuranceComponent({
  visitDetail,
  patientInfo,
  step,
  secondaryInsuranceStep,
  isLoading,
  currentUrl,
  referringUrl
}: Props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [updateChecklistInsuranceId, { loading }] = useMutation(UPDATE_CHECKLIST_INSURANCE_ID);
  const [infoCorrect, setInfoCorrect] = useState<YesNo>('yes');
  const [electSelfPay, setElectSelfPay] = useState(false);
  const [hasSecondaryImages, sethasSecondaryImages] = useState(false);
  const [payload, setPayload] = useState<CardUploadPayload>();
  const [warnSecondaryOnly, setWarnSecondaryOnly] = useState(false);
  const [isCorrect, setIsCorrect] = useState('');

  useEffect(() => {
    if (!step) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const submitEnabled = getSubmitStatus(infoCorrect, electSelfPay, hasSecondaryImages, payload);
  const insurances: Insurance[] = visitDetail?.insurance || [];

  const onSend = async () => {
    const mutationPayload = composePayload(
      step,
      visitDetail,
      patientInfo,
      isCorrect,
      { primary: payload?.primary },
      electSelfPay
    );
    const res = await updateChecklistInsuranceId({
      variables: {
        payload: mutationPayload
      },
      context: {
        clientName: 'orchestration',
        headers: headerWithJWT()
      }
    });

    if (payload?.secondary?.cardFront?.azureFileName) {
      const mutationPayload2 = composePayload(
        secondaryInsuranceStep || step,
        visitDetail,
        patientInfo,
        isCorrect,
        { secondary: payload.secondary },
        electSelfPay
      );
      const res2 = await updateChecklistInsuranceId({
        variables: {
          payload: mutationPayload2
        },
        context: {
          clientName: 'orchestration',
          headers: headerWithJWT()
        }
      });

      if (!res2.data.updateChecklistInsuranceStep.success) {
        handleError(errors.InsuranceSecondary, history);
      }
    }

    if (res.data.updateChecklistInsuranceStep.success) {
      dispatch({
        type: UPDATE_PRE_REGISTRATION_CHECKLIST,
        payload: res.data.updateChecklistInsuranceStep.data
      });
      dispatch(
        updateSkipPaymentFlag({
          key: SkipPaymentFlags.INSURANCE_INFO_CHANGED,
          value: !!mutationPayload.stepData.images.length || isCorrect === 'No'
        })
      );
      dispatch(
        updateSkipPaymentFlag({
          key: SkipPaymentFlags.SELF_PAY,
          value: electSelfPay
        })
      );
      dispatch(
        updateSkipPaymentFlag({
          key: SkipPaymentFlags.SELF_PAY_ELECT,
          value: infoCorrect === 'yes' && electSelfPay
        })
      );

      const updateInsurance = !electSelfPay && infoCorrect === 'no' ? 'Yes' : 'No';
      const updateSecondaryInsurance = hasSecondaryImages && !electSelfPay ? 'Yes' : 'No';
      const hasInsurance = infoCorrect === 'no' && electSelfPay ? 'No' : 'Yes';
      const useInsurance = electSelfPay ? 'No' : 'Yes';
      const additionalInfo = `Updated Primary Insurance (${updateInsurance}). Update Secondary Insurance (${updateSecondaryInsurance}). Has insurance (${hasInsurance}). Use insurance (${useInsurance}) Is this information correct? (${isCorrect})`;
      const amplitudeEventData: AmplitudeEventData = {
        currentUrl,
        referringUrl,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_location: visitDetail?.location?.name || '',
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_detail_type: `Hospital Registration: ${step.title}`,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_detail_additional_info: additionalInfo
      };
      analyticsService.logEvent(AnalyticsEvent.HospitalFormDetailComplete, amplitudeEventData);

      history.push('/pre-reg/checklist');
    } else {
      handleError(errors.Insurance, history);
    }
  };

  const onConfirmButtonPress = () => {
    try {
      if (submitEnabled) {
        if (step.complete) {
          handleError(errors.Complete, history);
        } else if (
          payload?.secondary?.cardFront?.azureFileName &&
          !payload.primary?.cardFront?.azureFileName
        ) {
          setWarnSecondaryOnly(true);
        } else {
          onSend();
        }
      } else {
        handleError(errors.Insurance, history);
      }
    } catch (err) {
      handleError(errors.Insurance, history);
    }
  };

  const primaryInsurance = insurances.find(ins => ins.insuranceOrder === 1); // { name: 'Select Health', groupId: '818-1983', memberId: '182-413-181' };
  const secondaryInsurance = insurances.find(ins => ins.insuranceOrder !== 1); // { name: 'Igors slab', groupId: '818-1983', memberId: '182-413-181' };
  const electSelfPayText = 'I do not want to use insurance for this visit.';
  const outOfNetwork = visitDetail?.activity?.isPlanOutOfNetwork;
  const isSelfPay = !insurances.length || selfPayInsuranceCodes.includes(insurances[0].plancode);

  const handleInsuranceCorrectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCorrect((event.target as HTMLInputElement).value);
  };

  return (
    <FlexBoxColumn data-testid="pre-registeration-insurance" minHeight="100%" flex="1 1 auto">
      <PreRegConfirmModal
        confirmLabel="No, thank you"
        onConfirm={() => {
          setWarnSecondaryOnly(false);
          onSend();
        }}
        cancelLabel="Yes, please"
        onCancel={() => setWarnSecondaryOnly(false)}
        isOpen={warnSecondaryOnly}
        description="You have only uploaded Secondary Insurance card images. Do you also want to upload Primary Insurance card images?"
      />
      <SpinnerModal isLoading={isLoading || loading} />
      <PreRegHeader title="Insurance" />

      <FlexBox padding={Spacing.large}>
        <DefaultText fontWeight="bold">
          Please confirm that your insurance plan information is correct.
          {/* TODO: update the header sentence once there is business logic to allow for these options for the 5.13.0 release  */}
          {/* {infoCorrect === 'yes'
            ? 'Please confirm that your insurance plan information is correct or upload your new insurance plan card(s).'
            : 'Please upload your insurance card'} */}
        </DefaultText>
      </FlexBox>
      {infoCorrect === 'yes' ? <Divider color={Color.grayLight3} /> : null}
      <Spacer size="small" />

      {infoCorrect !== 'no' ? (
        <div>
          <FlexBoxColumn justifyContent={secondaryInsurance ? 'center' : 'left'}>
            {!primaryInsurance && !secondaryInsurance ? (
              <MuiBox paddingLeft={`${Spacing.medium}px`}>
                <DefaultText>No Insurance</DefaultText>
              </MuiBox>
            ) : null}
            {primaryInsurance ? <InsuranceCard insurance={primaryInsurance} /> : null}
            {secondaryInsurance ? <InsuranceCard insurance={secondaryInsurance} /> : null}
          </FlexBoxColumn>
          {outOfNetwork ? (
            <FlexBoxRow data-testID="insurance-out-of-network">
              <FlexBox flex="1 1 auto">
                <Warning />
              </FlexBox>
              <FlexBox flex="1 1 auto" padding={`0px ${Spacing.large}px ${Spacing.large}px 0px`}>
                <DefaultText fontSize={FontSize.small} fontWeight="bold">
                  Out-of-Network
                </DefaultText>
                <DefaultText fontSize={FontSize.small}>
                  Your insurance is out-of-network for your visit location and may cost
                  significantly more than at an in-network location.
                </DefaultText>
                <Spacer size="small" />
              </FlexBox>
            </FlexBoxRow>
          ) : null}
        </div>
      ) : null}

      {infoCorrect === 'yes' ? (
        <Divider color={Color.grayLight3} />
      ) : (
        <FlexBoxColumn>
          <UploadInsurance
            isPrimary
            onChange={(primary: CardInfo) => {
              const old = payload || {};
              setPayload({ ...old, primary });
            }}
          />
          <div>
            <MuiCheckbox
              color="primary"
              aria-label="I have secondary insurance"
              value={hasSecondaryImages}
              onChange={(evt: SyntheticEvent, val: boolean) => sethasSecondaryImages(val)}
            />
            I have secondary insurance information
          </div>
          {hasSecondaryImages ? (
            <UploadInsurance
              onChange={(secondary: CardInfo) => {
                const old = payload || ({} as CardUploadPayload);
                setPayload({ ...old, secondary });
              }}
            />
          ) : null}
        </FlexBoxColumn>
      )}
      <FlexBoxColumn padding={`0px ${Spacing.large}px`}>
        <MuiBox>
          <Spacer size="medium" />
          <Typography>
            Is this information correct? <StyledAsterisk />
          </Typography>
          <MuiRadioGroup value={isCorrect} onChange={handleInsuranceCorrectChange}>
            {INFORMATION_IS_CORRECT_OPTIONS.map(option => {
              return (
                <MuiBox key={option.value} mr={4}>
                  <MuiFormControlLabel
                    data-testid={`${option.value} radio button`}
                    value={option.value}
                    control={<MuiRadio color="primary" />}
                    label={option.label}
                  />
                </MuiBox>
              );
            })}
          </MuiRadioGroup>
        </MuiBox>
      </FlexBoxColumn>
      {isCorrect === 'No' ? (
        <FlexBoxColumn padding={`0px ${Spacing.large}px`}>
          <MuiBox>
            <Spacer size="medium" />
            <Typography>
              You will be asked to update your insurance information when you arrive at your visit.
            </Typography>
          </MuiBox>
        </FlexBoxColumn>
      ) : null}
      <FlexBoxColumn justifyContent="flex-end" flex="1 1 auto" padding={`0px ${Spacing.large}px`}>
        {!isSelfPay && infoCorrect === 'yes' ? (
          <MuiFormControlLabel
            data-testid="electSelfPayCheckboxControl"
            label={electSelfPayText}
            control={
              <MuiCheckbox
                data-testid="electSelfPayCheckbox"
                aria-label={electSelfPayText}
                value={electSelfPay}
                onChange={(evt: SyntheticEvent, val: boolean) => {
                  setElectSelfPay(val);
                }}
              />
            }
          />
        ) : null}

        <StyledButton
          onPress={onConfirmButtonPress}
          disabled={!submitEnabled || isCorrect === ''}
          data-testid="Insurance-Confirm-Button"
        >
          Confirm
        </StyledButton>
        {ALLOW_UPLOAD ? (
          <StyledButton
            flat
            onClick={() => {
              if (infoCorrect === 'no') {
                setInfoCorrect('yes');
              } else {
                setInfoCorrect('no');
                setPayload({} as CardUploadPayload);
              }
            }}
          >
            {infoCorrect === 'no' ? 'Cancel' : 'Update my insurance information'}
          </StyledButton>
        ) : (
          <Spacer size="medium" />
        )}
      </FlexBoxColumn>
    </FlexBoxColumn>
  );
}

const mapStateToProps = (state: RootState, props: CheckListItemPageProps) => {
  const id = props.location.state?.id || '';
  return {
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    secondaryInsuranceStep: PreRegistrationSecondaryInsuranceSelector(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};
export default connect(mapStateToProps)(PreRegisterationInsuranceComponent);
